/**
 * API endpoints para Equipment Types, Maintenances y Assignments
 * Base URL: /api/equipment/
 */

import { GET, POST, PUT, DELETE } from "../config";

// ==================== EQUIPMENT TYPES ====================

/**
 * Listar tipos de equipos
 * @param {object} filters - Filtros (is_active)
 * @returns {Promise} Lista de tipos
 */
export const listTypes = async (filters = {}) => {
  let url = "equipment/types/?page_size=100";

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un tipo de equipo por ID
 * @param {string|number} id - ID del tipo
 * @returns {Promise} Datos del tipo
 */
export const getType = async (id) => {
  const response = await GET(`equipment/types/${id}/`);
  return response.data;
};

/**
 * Crear un tipo de equipo
 * @param {object} data - Datos del tipo
 * @returns {Promise} Tipo creado
 */
export const createType = async (data) => {
  const response = await POST("equipment/types/", data);
  return response.data;
};

/**
 * Actualizar un tipo de equipo
 * @param {string|number} id - ID del tipo
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Tipo actualizado
 */
export const updateType = async (id, data) => {
  const response = await PUT(`equipment/types/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un tipo de equipo
 * @param {string|number} id - ID del tipo
 * @returns {Promise}
 */
export const deleteType = async (id) => {
  const response = await DELETE(`equipment/types/${id}/`);
  return response.data;
};

// ==================== MAINTENANCES ====================

/**
 * Listar mantenimientos
 * @param {object} filters - Filtros (equipment, status, type, date_from, date_to)
 * @returns {Promise} Lista de mantenimientos
 */
export const listMaintenances = async (filters = {}) => {
  let url = "equipment/maintenances/?page_size=50";

  if (filters.equipment) {
    url += `&equipment=${filters.equipment}`;
  }

  if (filters.status) {
    url += `&status=${filters.status}`;
  }

  if (filters.maintenance_type) {
    url += `&maintenance_type=${filters.maintenance_type}`;
  }

  if (filters.date_from) {
    url += `&scheduled_date__gte=${filters.date_from}`;
  }

  if (filters.date_to) {
    url += `&scheduled_date__lte=${filters.date_to}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un mantenimiento por ID
 * @param {string|number} id - ID del mantenimiento
 * @returns {Promise} Datos del mantenimiento
 */
export const getMaintenance = async (id) => {
  const response = await GET(`equipment/maintenances/${id}/`);
  return response.data;
};

/**
 * Crear un mantenimiento
 * @param {object} data - Datos del mantenimiento
 * @returns {Promise} Mantenimiento creado
 */
export const createMaintenance = async (data) => {
  const response = await POST("equipment/maintenances/", data);
  return response.data;
};

/**
 * Actualizar un mantenimiento
 * @param {string|number} id - ID del mantenimiento
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Mantenimiento actualizado
 */
export const updateMaintenance = async (id, data) => {
  const response = await PUT(`equipment/maintenances/${id}/`, data);
  return response.data;
};

/**
 * Completar un mantenimiento
 * @param {string|number} id - ID del mantenimiento
 * @param {object} data - Datos de finalización (cost, notes, completion_date)
 * @returns {Promise} Mantenimiento completado
 */
export const completeMaintenance = async (id, data) => {
  const response = await POST(`equipment/maintenances/${id}/complete/`, data);
  return response.data;
};

/**
 * Cancelar un mantenimiento
 * @param {string|number} id - ID del mantenimiento
 * @param {string} reason - Razón de cancelación
 * @returns {Promise}
 */
export const cancelMaintenance = async (id, reason) => {
  const response = await POST(`equipment/maintenances/${id}/cancel/`, {
    reason,
  });
  return response.data;
};

/**
 * Eliminar un mantenimiento
 * @param {string|number} id - ID del mantenimiento
 * @returns {Promise}
 */
export const deleteMaintenance = async (id) => {
  const response = await DELETE(`equipment/maintenances/${id}/`);
  return response.data;
};

// ==================== ASSIGNMENTS ====================

/**
 * Listar asignaciones de equipos
 * @param {object} filters - Filtros (equipment, employee, branch, is_active)
 * @returns {Promise} Lista de asignaciones
 */
export const listAssignments = async (filters = {}) => {
  let url = "equipment/assignments/?page_size=50";

  if (filters.equipment) {
    url += `&equipment=${filters.equipment}`;
  }

  if (filters.employee) {
    url += `&employee=${filters.employee}`;
  }

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener una asignación por ID
 * @param {string|number} id - ID de la asignación
 * @returns {Promise} Datos de la asignación
 */
export const getAssignment = async (id) => {
  const response = await GET(`equipment/assignments/${id}/`);
  return response.data;
};

/**
 * Crear una asignación
 * @param {object} data - Datos de la asignación
 * @returns {Promise} Asignación creada
 */
export const createAssignment = async (data) => {
  const response = await POST("equipment/assignments/", data);
  return response.data;
};

/**
 * Actualizar una asignación
 * @param {string|number} id - ID de la asignación
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Asignación actualizada
 */
export const updateAssignment = async (id, data) => {
  const response = await PUT(`equipment/assignments/${id}/`, data);
  return response.data;
};

/**
 * Finalizar/Devolver una asignación
 * @param {string|number} id - ID de la asignación
 * @param {object} data - Datos de devolución (return_date, condition, notes)
 * @returns {Promise} Asignación finalizada
 */
export const returnAssignment = async (id, data) => {
  const response = await POST(`equipment/assignments/${id}/return/`, data);
  return response.data;
};

/**
 * Eliminar una asignación
 * @param {string|number} id - ID de la asignación
 * @returns {Promise}
 */
export const deleteAssignment = async (id) => {
  const response = await DELETE(`equipment/assignments/${id}/`);
  return response.data;
};

// ==================== EXPORT ====================

const equipmentTypesApi = {
  // Types
  listTypes,
  getType,
  createType,
  updateType,
  deleteType,
  // Maintenances
  listMaintenances,
  getMaintenance,
  createMaintenance,
  updateMaintenance,
  completeMaintenance,
  cancelMaintenance,
  deleteMaintenance,
  // Assignments
  listAssignments,
  getAssignment,
  createAssignment,
  updateAssignment,
  returnAssignment,
  deleteAssignment,
};

export default equipmentTypesApi;
